(() => {
  const CONFIG = Object.assign(
    {
      enabled: false,
      apiBase: 'https://joix.search.zetacx.net/api/la',
      legacyHost: 'https://www.mens.lanvin-collection.com',
      perPage: 24,
      suggestEnabled: true,
      searchUrl: '/search',
      moneyFormat: '¥{{amount_no_decimals_with_comma_separator}}',
    },
    window.ZetaConfig || {},
  );

  const FACET_TYPES = [
    'brand',
    'category',
    'size',
    'fit',
    'color',
    'pattern',
    'collection',
    'price',
    'type',
    'item_stock',
    'material',
  ];

  // スライダー上限の算出に使う「価格が高い順」のソートキー。単数形の sale_price は 400 になるので使わない。
  const MAXIMUM_PRICE_SORT = '-sales_price';
  // 商品情報の実在確認は1ページ最大50件になるため、同時リクエスト数を絞る。
  const PRODUCT_FETCH_CONCURRENCY = 6;
  const FALLBACK_IMAGE_WIDTH = 1000;
  // カラー問い合わせはカード枚数ぶん発生するため、同時リクエスト数を絞る。
  const COLOR_SWATCH_FETCH_CONCURRENCY = 4;
  // 1商品のカラー展開は多くても十数件なので、取得上限を小さく固定する。
  const COLOR_SWATCH_FETCH_LIMIT = 20;
  // COLOR_SWATCH_MAP に無い系統色に使う色。
  const COLOR_SWATCH_FALLBACK = '#cccccc';
  const COLOR_OPTION_NAME_KEYWORDS = ['カラー', 'color', '色'];
  const productFetchCache = new Map();

  const COLOR_SWATCH_MAP = {
    'バーガンディ系': '#800020',
    'インディゴ系': '#263b66',
    'スミクロ系': '#333333',
    'ホワイト系': '#ffffff',
    'グレー系': '#d8d8d8',
    'ブラック系': '#000000',
    'ブラウン系': '#8f7a66',
    'ベージュ系': '#b8b09c',
    'グリーン系': '#283a1e',
    'ブルー系': '#4f73bb',
    'パープル系': '#7e4fbb',
    'レッド系': '#bb4f51',
    'オレンジ系': '#d69a6a',
    'ピンク系': '#b89c9c',
    'イエロー系': '#b9b145',
    'ネイビー系': '#1e233a',
    'サックス系': '#45b7b9',
    'その他': '#cccccc',
  };

  const COLOR_NAME_KEYWORD_MAP = [
    [['バーガンディ', 'バーガンディー', 'ボルドー', 'burgundy', 'bordeaux'], 'バーガンディ系'],
    [['インディゴ', 'indigo'], 'インディゴ系'],
    [['スミクロ', '墨黒'], 'スミクロ系'],
    [['ネイビー', '紺'], 'ネイビー系'],
    [['ブラック', '黒'], 'ブラック系'],
    [['チャコール', 'ダークグレー', 'グレー', '灰'], 'グレー系'],
    [['ホワイト', '白', 'アイボリー', 'エクリュ'], 'ホワイト系'],
    [['ブラウン', '茶'], 'ブラウン系'],
    [['ベージュ', 'カーキ'], 'ベージュ系'],
    [['グリーン', '緑'], 'グリーン系'],
    [['サックス', '水色'], 'サックス系'],
    [['ブルー', '青'], 'ブルー系'],
    [['パープル', '紫'], 'パープル系'],
    [['レッド', '赤', 'ワイン'], 'レッド系'],
    [['オレンジ', '橙'], 'オレンジ系'],
    [['ピンク'], 'ピンク系'],
    [['イエロー', '黄'], 'イエロー系'],
  ];

  function resolveColorSwatchColor(colorBaseName, colorName = '') {
    const specificName = String(colorName || colorBaseName || '').trim();
    const specificColor = COLOR_NAME_KEYWORD_MAP.slice(0, 3).find(([keywords]) => keywords.some((keyword) => specificName.includes(keyword)));
    if (specificColor) return COLOR_SWATCH_MAP[specificColor[1]];
    if (COLOR_SWATCH_MAP[colorBaseName]) {
      return COLOR_SWATCH_MAP[colorBaseName];
    }
    const normalizedName = String(colorName || colorBaseName || '').trim();
    const matchedEntry = COLOR_NAME_KEYWORD_MAP.find(([keywords]) => keywords.some((keyword) => normalizedName.includes(keyword)));
    return matchedEntry ? COLOR_SWATCH_MAP[matchedEntry[1]] : COLOR_SWATCH_FALLBACK;
  }

  function formatMoney(yen) {
    if (yen == null || String(yen).trim() === '') {
      return '';
    }
    const amount = Number(yen);
    if (!Number.isFinite(amount)) {
      return '';
    }
    return `¥${amount.toLocaleString('ja-JP')}`;
  }

  async function fetchShopifyProduct(itemId) {
    const handle = String(itemId).toLowerCase();
    if (productFetchCache.has(handle)) {
      return await productFetchCache.get(handle);
    }

    const productPromise = (async () => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!response.ok) {
          return null;
        }
        return await response.json();
      } catch {
        return null;
      }
    })();
    productFetchCache.set(handle, productPromise);
    return await productPromise;
  }

  // ZETAのnew_flgが未更新でも、Shopifyの商品タグを正としてNEW表示を補完する。
  async function attachNewBadge(labels, itemId) {
    if (!labels || labels.querySelector('.label--new')) {
      return;
    }
    const product = await fetchShopifyProduct(itemId);
    const tags = Array.isArray(product?.tags)
      ? product.tags
      : String(product?.tags || '').split(',');
    if (!tags.some((tag) => String(tag).trim().toLowerCase() === 'new')) {
      return;
    }
    const badge = document.createElement('span');
    badge.className = 'label label--new';
    badge.textContent = 'NEW';
    labels.prepend(badge);
  }

  function resolveVariantId(product, wishlistTarget) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const targetSku = String(wishlistTarget?.sku || '');

    // Shopify側のSKU規則が未検証のため、最も確実なZETA SKUの完全一致を優先する。
    if (targetSku) {
      const exactSkuVariant = variants.find((variant) => variant.sku === targetSku);
      if (exactSkuVariant) {
        return exactSkuVariant.id;
      }
    }

    const itemId = String(wishlistTarget?.itemId || '');
    const colorCode = String(wishlistTarget?.colorCode || '');
    // 完全一致しない場合も同じ色の別サイズを拾えるよう、商品IDと色コードのSKU接頭辞で照合する。
    if (itemId && colorCode) {
      const skuPrefix = `${itemId}${colorCode}`;
      const colorSkuVariant = variants.find((variant) => String(variant.sku || '').startsWith(skuPrefix));
      if (colorSkuVariant) {
        return colorSkuVariant.id;
      }
    }

    const colorName = String(wishlistTarget?.colorName || '').trim();
    const productOptions = Array.isArray(product?.options) ? product.options : [];
    // SKU規則で特定できない場合に備え、Shopifyの商品オプション名とZETAカラー名でも照合する。
    if (colorName) {
      const colorOptionIndex = productOptions.findIndex((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const normalizedOptionName = String(optionName || '').trim().toLowerCase();
        return COLOR_OPTION_NAME_KEYWORDS.some((keyword) => normalizedOptionName.includes(keyword));
      });

      if (colorOptionIndex >= 0) {
        const colorOption = productOptions[colorOptionIndex];
        const configuredPosition = typeof colorOption === 'object' ? Number(colorOption?.position) : 0;
        const optionPosition = configuredPosition >= 1 && configuredPosition <= 3 ? configuredPosition : colorOptionIndex + 1;
        const optionProperty = `option${optionPosition}`;
        const colorOptionVariant = variants.find((variant) => String(variant[optionProperty] || '').trim() === colorName);
        if (colorOptionVariant) {
          return colorOptionVariant.id;
        }
      }
    }

    return product?.variants?.[0]?.id ?? null;
  }

  function resolveColorVariants(product, wishlistTarget) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const itemId = String(wishlistTarget?.itemId || '');
    const colorCode = String(wishlistTarget?.colorCode || '');

    if (itemId && colorCode) {
      const skuPrefix = `${itemId}${colorCode}`;
      const colorSkuVariants = variants.filter((variant) => String(variant.sku || '').startsWith(skuPrefix));
      if (colorSkuVariants.length > 0) {
        return colorSkuVariants;
      }
    }

    const colorName = String(wishlistTarget?.colorName || '').trim();
    const productOptions = Array.isArray(product?.options) ? product.options : [];
    if (colorName) {
      const colorOptionIndex = productOptions.findIndex((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const normalizedOptionName = String(optionName || '').trim().toLowerCase();
        return COLOR_OPTION_NAME_KEYWORDS.some((keyword) => normalizedOptionName.includes(keyword));
      });

      if (colorOptionIndex >= 0) {
        const colorOption = productOptions[colorOptionIndex];
        const configuredPosition = typeof colorOption === 'object' ? Number(colorOption?.position) : 0;
        const optionPosition = configuredPosition >= 1 && configuredPosition <= 3 ? configuredPosition : colorOptionIndex + 1;
        const optionProperty = `option${optionPosition}`;
        return variants.filter((variant) => String(variant[optionProperty] || '').trim() === colorName);
      }
    }

    return [];
  }

  // カラー単位の在庫判定。カラーを特定できないときは品番単位へフォールバックする。
  function resolveColorAvailability(product, wishlistTarget) {
    const colorVariants = resolveColorVariants(product, wishlistTarget);
    if (colorVariants.length > 0) {
      return colorVariants.some((variant) => variant.available === true);
    }
    return product.available === true;
  }

  // カード遷移用。カラーを確実に特定できたときだけIDを返す（失敗時はURLを変えない）。
  function resolveLinkVariantId(product, wishlistTarget) {
    const colorVariants = resolveColorVariants(product, wishlistTarget);
    if (colorVariants.length === 0) {
      return null;
    }
    const availableVariant = colorVariants.find((variant) => variant.available === true);
    return (availableVariant || colorVariants[0]).id ?? null;
  }

  // 商品URLへバリアント指定を足す。値が無ければ元のURLをそのまま返す。
  function appendVariantToUrl(url, variantId) {
    if (!url || variantId == null) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}variant=${encodeURIComponent(String(variantId))}`;
  }

  // Shopify画像URLへカード表示用の幅パラメータを付ける。
  function withImageWidth(url) {
    if (typeof url !== 'string' || url.trim() === '') {
      return '';
    }
    try {
      const imageUrl = new URL(url, window.location.href);
      imageUrl.searchParams.set('width', String(FALLBACK_IMAGE_WIDTH));
      return imageUrl.toString();
    } catch {
      return '';
    }
  }

  // Shopifyの重複アップロードUUIDを除き、画像のカット識別子を返す。
  function resolveImageCutKey(url) {
    if (!url) {
      return '';
    }

    let pathname;
    try {
      pathname = new URL(url, window.location.href).pathname;
    } catch {
      pathname = String(url);
    }

    const filename = pathname.split('/').pop() || '';
    return filename
      .replace(/\.[^./]+$/, '')
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  }

  // ホバー画像の再開時に使うため、異なるカットを選ぶ処理は一時的に残す。
  function findDifferentCutImageUrl(imageUrls, baseImageUrl) {
    if (!Array.isArray(imageUrls)) {
      return '';
    }

    const baseImageCutKey = resolveImageCutKey(baseImageUrl);
    return imageUrls.find((imageUrl) => imageUrl && resolveImageCutKey(imageUrl) !== baseImageCutKey) || '';
  }

  // ZETAのカラー情報に対応するShopifyの主画像を返す。
  function resolveShopifyImageUrls(product, wishlistTarget) {
    const colorVariants = resolveColorVariants(product, wishlistTarget);
    const variantWithImage = colorVariants.find((variant) => variant.featured_image?.src);
    const primaryImageUrl = variantWithImage?.featured_image?.src || product?.featured_image || product?.images?.[0] || '';
    return {
      primary: withImageWidth(primaryImageUrl),
      hover: '',
    };
  }

  // 画像URL未設定または読み込み失敗済みかを判定する。
  function isImageBroken(img) {
    return !img.getAttribute('src') || (img.complete && img.naturalWidth === 0);
  }

  // 壊れた画像へShopify画像を一度だけ適用する。
  function applyImageFallback(img, url, alt) {
    if (img.dataset.zetaImageFallback === '1') {
      return;
    }
    if (!url) {
      img.remove();
      return;
    }
    img.dataset.zetaImageFallback = '1';
    img.src = url;
    if (alt && !img.alt) {
      img.alt = alt;
    }
  }

  // 現在と遅延読み込み後の両方で画像フォールバックを適用する。
  function prepareImageFallback(img, url, alt = '') {
    if (!img) {
      return;
    }
    if (isImageBroken(img)) {
      applyImageFallback(img, url, alt);
      return;
    }
    img.addEventListener('error', () => applyImageFallback(img, url, alt), { once: true });
  }

  // 主画像の破損時に主画像とホバー画像をShopify画像のペアへ揃える。
  function prepareImagePairFallback(primaryImage, hoverImage, imageUrls, alt = '') {
    const applyImagePairFallback = () => {
      if (primaryImage) {
        applyImageFallback(primaryImage, imageUrls.primary, alt);
      }
      if (hoverImage) {
        applyImageFallback(hoverImage, imageUrls.hover);
      }
    };

    if (!primaryImage) {
      prepareImageFallback(hoverImage, imageUrls.hover);
      return;
    }
    if (isImageBroken(primaryImage)) {
      applyImagePairFallback();
      return;
    }

    primaryImage.addEventListener('error', applyImagePairFallback, { once: true });
    prepareImageFallback(hoverImage, imageUrls.hover);
  }

  // 壊れたZETA商品画像だけをShopify商品JSONの画像で修復する。
  async function repairProductImages(container, options = {}) {
    const { imageSelector = '[data-zeta-image]' } = options;
    const images = Array.from(container.querySelectorAll(imageSelector)).filter(
      (img) => img.tagName === 'IMG' && img.dataset.zetaItemId && img.dataset.zetaImageRepair !== '1',
    );
    const brokenImages = [];

    const repairImage = async (img) => {
      const itemId = img.dataset.zetaItemId;
      const product = await fetchShopifyProduct(itemId);
      if (!product) {
        img.remove();
        return;
      }
      const imageUrls = resolveShopifyImageUrls(product, {
        itemId,
        sku: img.dataset.zetaSku,
        colorCode: img.dataset.zetaColorCode,
        colorName: img.dataset.zetaColorName,
      });
      applyImageFallback(img, imageUrls.primary, img.dataset.zetaImageAlt || '');
    };

    images.forEach((img) => {
      img.dataset.zetaImageRepair = '1';
      if (isImageBroken(img)) {
        brokenImages.push(img);
        return;
      }
      img.addEventListener('error', async () => await repairImage(img), { once: true });
    });

    for (let index = 0; index < brokenImages.length; index += PRODUCT_FETCH_CONCURRENCY) {
      const imageChunk = brokenImages.slice(index, index + PRODUCT_FETCH_CONCURRENCY);
      await Promise.all(imageChunk.map(repairImage));
    }
  }

  async function attachWishlistButtons(container, options = {}) {
    const soldSelector = options.soldSelector;
    const imageSelector = options.imageSelector || '[data-zeta-image]';
    const hoverImageSelector = options.hoverImageSelector === undefined ? '[data-zeta-image-hover]' : options.hoverImageSelector;
    const cardRootSelector = options.cardRootSelector;
    const linkSelectors = options.linkSelectors;
    // お気に入りボタンの二重生成は下の早期returnで防ぐため、ここでは全スロットを処理する。
    const slots = Array.from(container.querySelectorAll('[data-zeta-wishlist-slot][data-zeta-item-id]'));

    for (let index = 0; index < slots.length; index += PRODUCT_FETCH_CONCURRENCY) {
      const slotChunk = slots.slice(index, index + PRODUCT_FETCH_CONCURRENCY);
      await Promise.all(
        slotChunk.map(async (slot) => {
          const wishlistTarget = {
            itemId: slot.dataset.zetaItemId,
            sku: slot.dataset.zetaSku,
            colorCode: slot.dataset.zetaColorCode,
            colorName: slot.dataset.zetaColorName,
          };
          const product = await fetchShopifyProduct(wishlistTarget.itemId);
          if (!slot.isConnected) {
            return;
          }

          const image = slot.querySelector(imageSelector);
          const hoverImage = hoverImageSelector ? slot.querySelector(hoverImageSelector) : null;
          if (product) {
            const imageUrls = resolveShopifyImageUrls(product, wishlistTarget);
            const colorName = String(slot.dataset.zetaColorName || '').trim();
            const imageAlt = colorName ? `${product.title} ${colorName}` : product.title;
            prepareImagePairFallback(image, hoverImage, imageUrls, imageAlt);
          } else {
            if (image && !image.getAttribute('src')) {
              image.remove();
            }
            if (hoverImage && !hoverImage.getAttribute('src')) {
              hoverImage.remove();
            }
          }

          if (soldSelector) {
            const soldElement = slot.querySelector(soldSelector);
            if (product) {
              const isAvailable = resolveColorAvailability(product, wishlistTarget);
              slot.dataset.zetaSoldResolved = isAvailable ? '0' : '1';
              if (soldElement) {
                soldElement.hidden = isAvailable;
              }
            } else {
              slot.dataset.zetaSoldResolved = slot.dataset.zetaSoldOut === '1' ? '1' : '0';
              if (soldElement) {
                soldElement.hidden = slot.dataset.zetaSoldOut !== '1';
              }
            }
          }

          if (product && cardRootSelector && Array.isArray(linkSelectors) && linkSelectors.length > 0) {
            const linkVariantId = resolveLinkVariantId(product, wishlistTarget);
            if (linkVariantId != null) {
              const cardRoot = slot.closest(cardRootSelector);
              if (cardRoot) {
                linkSelectors.forEach((linkSelector) => {
                  cardRoot.querySelectorAll(linkSelector).forEach((link) => {
                    if (link.dataset.zetaVariantLink === '1') {
                      return;
                    }
                    link.dataset.zetaVariantLink = '1';
                    link.href = appendVariantToUrl(link.getAttribute('href'), linkVariantId);
                  });
                });
              }
            }
          }

          if (!product || slot.querySelector('wishlist-button-collection')) {
            return;
          }

          const variantId = resolveVariantId(product, wishlistTarget);
          const wishlistButton = document.createElement('wishlist-button-collection');
          wishlistButton.className = 'c-product-card__wishlist';
          wishlistButton.setAttribute('data-product-handle', product.handle);
          if (variantId != null) {
            wishlistButton.setAttribute('data-variant-id', String(variantId));
          }
          wishlistButton.setAttribute('show-icon', '');
          wishlistButton.setAttribute('icon', 'wishlist');
          slot.appendChild(wishlistButton);
        }),
      );
    }
  }

  function formatPriceFacet(value) {
    const [minimum, maximum] = String(value || '').split('-');
    if (!minimum && !maximum) {
      return String(value || '');
    }
    const formattedMinimum = minimum && Number(minimum) > 0 ? formatMoney(minimum) : '';
    const formattedMaximum = maximum ? formatMoney(maximum) : '';
    return `${formattedMinimum}〜${formattedMaximum}`;
  }

  function resolveLegacyUrl(path) {
    if (!path) {
      return '';
    }
    if (/^https?:\/\//i.test(path)) {
      return path;
    }
    const normalizedPath = path.charAt(0) === '/' ? path : `/${path}`;
    if (normalizedPath.startsWith('/products/') || normalizedPath.startsWith('/collections/')) {
      return normalizedPath;
    }
    if (!CONFIG.legacyHost) {
      return normalizedPath;
    }
    return `${CONFIG.legacyHost}${normalizedPath}`;
  }

  function normalizeItem(raw) {
    const discountRate = Number(raw.discount_rate) || 0;
    const bargainPrice = raw.bargain_price === '' || raw.bargain_price == null ? null : Number(raw.bargain_price);
    const salePrice = raw.sale_price === '' || raw.sale_price == null ? null : Number(raw.sale_price);
    // セール判定は sale_flg ではなく discount_rate>0。実売=bargain_price / 取消線=sale_price。
    const onSale = discountRate > 0 && bargainPrice != null;
    const imageUrls = (Array.isArray(raw.item_img_url_all) ? raw.item_img_url_all : [])
      .map(resolveLegacyUrl)
      .filter(Boolean);
    const imageUrl = resolveLegacyUrl(raw.item_img_url) || imageUrls[0] || '';

    const badges = [];
    if (Number(raw.new_flg) === 1) {
      badges.push({ key: 'new', label: 'NEW' });
    }
    if (Number(raw.reserve_flg) === 1) {
      badges.push({ key: 'reserve', label: 'PRE-ORDER' });
    }
    if (raw.restock_flg !== '' && raw.restock_flg != null && Number(raw.restock_flg) !== 0) {
      badges.push({ key: 'restock', label: 'RESTOCK' });
    }
    if (onSale) {
      badges.push({ key: 'sale', label: `${discountRate}%OFF` });
    }
    if (Number(raw.item_stock_cnt) <= 0) {
      badges.push({ key: 'soldout', label: 'SOLD OUT' });
    }

    return {
      id: raw.item_id,
      idColorCd: raw.item_id_color_cd,
      sku: raw.sku_cd,
      title: raw.item_nm || '',
      brand: raw.brand_nm || '',
      colorName: raw.color_nm || raw.color_base_nm || '',
      colorPosition: raw.color_position !== '' && raw.color_position != null && Number.isFinite(Number(raw.color_position)) ? Number(raw.color_position) : null,
      colorCode: raw.color_cd || '',
      colorBaseName: raw.color_base_nm || '',
      sizeName: raw.size_nm || '',
      price: onSale ? bargainPrice : salePrice,
      compareAtPrice: onSale ? salePrice : null,
      discountRate: discountRate,
      onSale: onSale,
      soldOut: Number(raw.item_stock_cnt) <= 0,
      imageUrl: imageUrl,
      imageLargeUrl: resolveLegacyUrl(raw.item_img_large_url),
      imageUrls: imageUrls,
      // ZETAのdesc_urlはdev・本番ともShopify商品URLに使えないため、item_idから組み立てる。
      url: raw.item_id ? `/products/${encodeURIComponent(String(raw.item_id).toLowerCase())}` : '',
      badges: badges,
    };
  }

  // stock と color_info は ZETA で必須のため常に付与する。
  function buildItemQuery(params) {
    const search = new URLSearchParams();
    search.set('q', params.q || '');
    search.set('l', String(params.l != null ? params.l : CONFIG.perPage));
    search.set('o', String(params.o || 0));
    // m を送るとフィールドが絞られ item_img_url_all 等が返らないため指定しない。
    search.set('s', params.s || '-score');
    search.set('stock', String(params.stock != null ? params.stock : 0));
    search.set('color_info', '0');
    search.set('c', FACET_TYPES.join(','));
    search.set('f', '0');

    const filters = params.filters || {};
    Object.keys(filters).forEach((field) => {
      const values = filters[field];
      if (Array.isArray(values) && values.length > 0) {
        search.set(field, values.join(','));
      }
    });

    return `?${search.toString()}`;
  }

  async function request(url) {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`ZETA API ${response.status}: ${text.slice(0, 200)}`);
    }
    return response.json();
  }

  async function fetchItems(params = {}) {
    const json = await request(`${CONFIG.apiBase}/item${buildItemQuery(params)}`);
    const result = json.result || {};
    const attribute = result.attribute || {};
    return {
      total: Number(attribute.total) || 0,
      count: Number(attribute.count) || 0,
      offset: Number(attribute.offset) || 0,
      items: (result.items || []).map(normalizeItem),
      facets: result.facet || {},
    };
  }

  async function fetchColorBaseNames(itemId) {
    if (!itemId) {
      return [];
    }

    try {
      const result = await fetchItems({ q: itemId, l: COLOR_SWATCH_FETCH_LIMIT, o: 0, stock: 0 });
      const colorBaseNames = [];
      const seenColorBaseNames = new Set();

      result.items
        .filter((item) => item.id === itemId)
        .forEach((item) => {
          const colorBaseName = String(item.colorBaseName || '').trim();
          if (!colorBaseName || seenColorBaseNames.has(colorBaseName)) {
            return;
          }
          seenColorBaseNames.add(colorBaseName);
          colorBaseNames.push(colorBaseName);
        });

      return colorBaseNames;
    } catch (error) {
      return [];
    }
  }

  async function fetchColorRows(itemId) {
    if (!itemId) {
      return [];
    }

    try {
      const result = await fetchItems({ q: itemId, l: COLOR_SWATCH_FETCH_LIMIT, o: 0, stock: 0 });
      const colorRows = [];
      const seenColorNames = new Set();

      result.items
        .filter((item) => item.id === itemId)
        .forEach((item) => {
          const colorName = String(item.colorName || '').trim();
          if (!colorName || seenColorNames.has(colorName)) {
            return;
          }
          seenColorNames.add(colorName);
          colorRows.push({ colorName, colorBaseName: item.colorBaseName });
        });

      return colorRows;
    } catch (error) {
      return [];
    }
  }

  async function fetchColorItems(itemId) {
    if (!itemId) {
      return [];
    }

    try {
      const result = await fetchItems({ q: itemId, l: COLOR_SWATCH_FETCH_LIMIT, o: 0, stock: 0 });
      const seenColors = new Set();
      return result.items
        .filter((item) => item.id === itemId)
        .sort((a, b) => (a.colorPosition ?? Number.MAX_SAFE_INTEGER) - (b.colorPosition ?? Number.MAX_SAFE_INTEGER))
        .filter((item) => {
          const colorKey = String(item.colorCode || item.colorName || item.idColorCd || '').trim();
          if (!colorKey || seenColors.has(colorKey)) {
            return false;
          }
          seenColors.add(colorKey);
          return true;
        });
    } catch (error) {
      return [];
    }
  }

  function renderColorSwatches(swatchListElement, colorBaseNames) {
    if (colorBaseNames.length === 0) {
      swatchListElement.remove();
      return;
    }

    swatchListElement.hidden = false;
    swatchListElement.setAttribute('aria-label', 'カラー展開');
    colorBaseNames.forEach((colorBaseName) => {
      const swatchItem = document.createElement('li');
      const swatchDot = document.createElement('span');
      swatchItem.className = 'c-product-card__swatch';
      swatchDot.className = 'c-product-card__swatch-dot';
      swatchDot.style.backgroundColor = resolveColorSwatchColor(colorBaseName);
      swatchDot.title = colorBaseName;
      swatchItem.appendChild(swatchDot);
      swatchListElement.appendChild(swatchItem);
    });
  }

  function renderInteractiveColorSwatches(swatchListElement, colorItems, activeColorKey, onActivate) {
    if (!swatchListElement || !Array.isArray(colorItems) || colorItems.length === 0) {
      swatchListElement?.remove();
      return;
    }

    const colorKey = (item) => String(item.colorCode || item.colorName || item.idColorCd || '').trim();
    const activate = (item, swatchDot) => {
      swatchListElement.querySelectorAll('.c-product-card__swatch-dot').forEach((dot) => {
        const selected = dot === swatchDot;
        dot.classList.toggle('is-selected', selected);
        dot.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      onActivate?.(item);
    };

    swatchListElement.replaceChildren();
    swatchListElement.hidden = false;
    swatchListElement.setAttribute('aria-label', 'カラー展開');
    colorItems.forEach((item) => {
      const swatchItem = document.createElement('li');
      const swatchDot = document.createElement('span');
      const itemColorKey = colorKey(item);
      const selected = itemColorKey === String(activeColorKey || '').trim();
      const label = String(item.colorName || item.colorBaseName || 'カラー').trim();
      swatchItem.className = 'c-product-card__swatch';
      swatchDot.className = 'c-product-card__swatch-dot';
      swatchDot.classList.toggle('is-selected', selected);
      swatchDot.style.backgroundColor = resolveColorSwatchColor(item.colorBaseName, item.colorName);
      swatchDot.title = label;
      swatchDot.tabIndex = 0;
      swatchDot.setAttribute('role', 'button');
      swatchDot.setAttribute('aria-label', `${label}を表示`);
      swatchDot.setAttribute('aria-pressed', selected ? 'true' : 'false');
      swatchDot.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        activate(item, swatchDot);
      });
      swatchDot.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        activate(item, swatchDot);
      });
      swatchItem.appendChild(swatchDot);
      swatchListElement.appendChild(swatchItem);
    });
  }

  async function attachColorSwatches(container) {
    const slots = Array.from(container.querySelectorAll('[data-zeta-swatch-slot][data-zeta-swatch-item-id]')).filter(
      (slot) => !slot.querySelector('li'),
    );

    for (let index = 0; index < slots.length; index += COLOR_SWATCH_FETCH_CONCURRENCY) {
      const slotChunk = slots.slice(index, index + COLOR_SWATCH_FETCH_CONCURRENCY);
      await Promise.all(
        slotChunk.map(async (slot) => {
          const colorBaseNames = await fetchColorBaseNames(slot.dataset.zetaSwatchItemId);
          if (!slot.isConnected) {
            return;
          }
          renderColorSwatches(slot, colorBaseNames);
        }),
      );
    }
  }

  // category の解決結果に依らずスライダー上限を一定にするため、カタログ全体から取得する。
  async function fetchMaximumPrice() {
    const json = await request(`${CONFIG.apiBase}/item${buildItemQuery({ q: '', l: 1, o: 0, s: MAXIMUM_PRICE_SORT })}`);
    const items = json.result?.items || [];
    if (items.length === 0) {
      return null;
    }
    const rawMaximumPrice = items[0].sale_price;
    if (rawMaximumPrice === '' || rawMaximumPrice == null) {
      return null;
    }
    const maximumPrice = Number(rawMaximumPrice);
    return Number.isFinite(maximumPrice) ? maximumPrice : null;
  }

  async function fetchSuggest(qWord, options = {}) {
    const search = new URLSearchParams();
    search.set('q_word', qWord || '');
    search.set('suggest_limit', String(options.suggestLimit != null ? options.suggestLimit : 10));
    search.set('popularword_limit', String(options.popularLimit != null ? options.popularLimit : 10));
    search.set('story_limit', '0');

    const json = await request(`${CONFIG.apiBase}/suggest?${search.toString()}`);
    return {
      suggestions: Array.isArray(json.suggestion) ? json.suggestion : [],
      popularWords: Array.isArray(json.popularwords) ? json.popularwords : [],
    };
  }

  function buildResultsUrl(keyword) {
    const search = new URLSearchParams();
    search.set('q', keyword || '');
    search.set('view', 'zeta');
    return `${CONFIG.searchUrl}?${search.toString()}`;
  }

  async function initializeFeaturedCollectionSwatches(container = document) {
    const cards = Array.from(container.querySelectorAll('.c-featured-collections .c-product-card')).filter(
      (card) => card.dataset.colorSwitchInitialized !== '1' && card.querySelector('.c-product-card__swatches'),
    );

    await Promise.all(cards.map(async (card) => {
      card.dataset.colorSwitchInitialized = '1';
      const wishlist = card.querySelector('wishlist-button-collection[data-product-handle]');
      const handle = wishlist?.dataset.productHandle;
      const product = await fetchShopifyProduct(handle);
      if (!product || !card.isConnected) {
        return;
      }
      const colorOptionIndex = (product.options || []).findIndex((option) => {
        const name = String(typeof option === 'string' ? option : option?.name || '').toLowerCase();
        return COLOR_OPTION_NAME_KEYWORDS.some((keyword) => name.includes(keyword));
      });
      if (colorOptionIndex < 0) {
        return;
      }
      const dots = Array.from(card.querySelectorAll('.c-product-card__swatch-dot'));
      const colorRows = await fetchColorRows(String(handle).toUpperCase());
      const image = card.querySelector('.c-product-card__image');
      const links = card.querySelectorAll('.c-product-card__image-link, .c-product-card__body-link');
      const productPath = `/products/${encodeURIComponent(handle)}`;
      const normalizeImageFilename = (url) => {
        if (!url) return '';
        try {
          const pathname = new URL(String(url).replace(/^\/\//, 'https://'), window.location.origin).pathname;
          return decodeURIComponent(pathname.split('/').pop() || '').toLowerCase();
        } catch (error) {
          return String(url).split('?')[0].split('/').pop().toLowerCase();
        }
      };
      const displayedImageFilename = normalizeImageFilename(image?.currentSrc || image?.src || product.featured_image);
      const initialVariant = (product.variants || []).find((candidate) =>
        normalizeImageFilename(candidate.featured_image?.src) === displayedImageFilename,
      );
      const initialColorName = String(initialVariant?.options?.[colorOptionIndex] || '').trim();

      dots.forEach((dot, index) => {
        const colorName = String(dot.title || '').trim();
        const variant = (product.variants || []).find((candidate) => String(candidate.options?.[colorOptionIndex] || '').trim() === colorName);
        const colorRow = colorRows.find((row) => String(row.colorName || '').trim() === colorName);
        dot.classList.remove('c-product-card__swatch-dot--empty');
        dot.style.backgroundColor = resolveColorSwatchColor(colorRow?.colorBaseName, colorName);
        const activate = () => {
          dots.forEach((item) => {
            const selected = item === dot;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          if (variant?.featured_image?.src && image) {
            image.src = variant.featured_image.src;
            image.removeAttribute('srcset');
            image.alt = colorName ? `${product.title} ${colorName}` : product.title;
          }
          const productUrl = variant?.id ? `${productPath}?variant=${variant.id}` : productPath;
          links.forEach((link) => { link.href = productUrl; });
          if (wishlist && variant?.id) {
            wishlist.dataset.variantId = String(variant.id);
            wishlist.setAttribute('data-variant-id', String(variant.id));
          }
        };
        dot.tabIndex = 0;
        dot.setAttribute('role', 'button');
        dot.setAttribute('aria-label', `${colorName || 'カラー'}を表示`);
        const initiallySelected = Boolean(initialColorName) && colorName === initialColorName;
        dot.setAttribute('aria-pressed', initiallySelected ? 'true' : 'false');
        dot.classList.toggle('is-selected', initiallySelected);
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          activate();
        });
        dot.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            activate();
          }
        });
      });
    }));
  }

  window.Zeta = {
    config: CONFIG,
    facetTypes: FACET_TYPES,
    colorSwatchMap: COLOR_SWATCH_MAP,
    formatMoney,
    formatPriceFacet,
    resolveLegacyUrl,
    resolveImageCutKey,
    findDifferentCutImageUrl,
    normalizeItem,
    buildItemQuery,
    fetchItems,
    fetchMaximumPrice,
    fetchSuggest,
    buildResultsUrl,
    fetchShopifyProduct,
    attachNewBadge,
    resolveColorAvailability,
    resolveLinkVariantId,
    appendVariantToUrl,
    attachWishlistButtons,
    repairProductImages,
    fetchColorBaseNames,
    fetchColorRows,
    fetchColorItems,
    renderColorSwatches,
    renderInteractiveColorSwatches,
    attachColorSwatches,
    initializeFeaturedCollectionSwatches,
  };

  void initializeFeaturedCollectionSwatches();
  document.addEventListener('shopify:section:load', (event) => {
    void initializeFeaturedCollectionSwatches(event.target);
  });
})();
